import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { Modal } from 'bootstrap/dist/js/bootstrap';
import { toast } from 'react-toastify';
import { CristalLoader } from 'helpers/generic';
import { getCommunity } from 'helpers/community';

const ModalStyled = styled.div`
	background: rgba(0, 0, 0, 0.4);
`;

const IbanSubtitle = styled.div`
	font-family: monospace;
	font-size: 12px;
	color: var(--bs-gray-600);
	letter-spacing: 0.5px;
	margin-top: 2px;
`;

const Table = styled.table`
	width: 100%;
	font-size: 12px;
	border-collapse: collapse;

	th {
		font-weight: 500;
		text-align: center;
		background: var(--bs-gray-100);
		padding: 8px 10px;
		border: 1px solid var(--bs-gray-300);
		white-space: nowrap;

		&:first-child {
			text-align: left;
		}
	}
`;

const PropertyCell = styled.td`
	padding: 8px 12px;
	border: 1px solid var(--bs-gray-300);
	vertical-align: middle;
	font-size: 12px;
	font-weight: 500;
	min-width: 160px;
	background: var(--bs-gray-50);
`;

const ConceptCell = styled.td`
	border: 1px solid var(--bs-gray-300);
	padding: 8px 10px;
	vertical-align: top;
	min-width: 150px;
	transition: background 0.1s;

	&.has-payer:hover {
		background: var(--bs-gray-100);
		cursor: pointer;
	}

	&.empty {
		vertical-align: middle;
		text-align: center;
		color: var(--bs-gray-400);
	}
`;

const PayerName = styled.div`
	font-size: 11px;
	color: var(--bs-gray-600);
	margin-bottom: 2px;

	em {
		font-style: italic;
	}
`;

const PayerIban = styled.div`
	font-family: monospace;
	font-size: 12px;
	color: var(--bs-gray-900);
	letter-spacing: 0.3px;
	margin-bottom: 5px;
	word-break: break-all;
`;

const CheckLabel = styled.label`
	display: flex;
	align-items: center;
	gap: 5px;
	font-size: 12px;
	cursor: pointer;
	margin: 0;
	user-select: none;

	input[type="checkbox"] {
		cursor: pointer;
		flex-shrink: 0;
	}
`;

const CONCEPTS = ['ordinary', 'consumption', 'extraordinary'];
const CONCEPT_LABELS = {
	ordinary:      'Cuota ordinaria',
	consumption:   'Consumos',
	extraordinary: 'Cuota extraordinaria',
};

let axiosCancelToken = null;

function formatIbanGroups(iban) {
	if (!iban) return '—';
	return iban.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
}

function getAccountLabel(account) {
	if (!account) return null;
	if (account.id === -1) return 'Pago al contado';
	if (account.id === -2) return 'Junto con ordinaria';
	return formatIbanGroups(account.iban);
}

export default function ReplaceBankAccountModal(props) {
	const { newBankAccount, properties, ownerId, onClose } = props;

	const modalRef = useRef(null);
	const [checked, setChecked] = useState(new Map());
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();

		const modal = new Modal(modalRef.current, { backdrop: false });

		const hiddenEvent = () => {
			modal._element.removeEventListener('hidden.bs.modal', hiddenEvent);
			modal.dispose();
			document.body.style.overflow = 'auto';
			document.body.style.paddingRight = '0';
			onClose();
		};

		modal._element.addEventListener('hidden.bs.modal', hiddenEvent);
		modal.show();

		return () => axiosCancelToken.cancel();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const toggleCheck = (key) => {
		setChecked(prev => {
			const next = new Map(prev);
			next.set(key, !next.get(key));
			return next;
		});
	};

	const checkedCount = [...checked.values()].filter(Boolean).length;

	const buildReplacements = () => {
		const byKey = {};
		for (const [key, isChecked] of checked.entries()) {
			if (!isChecked) continue;
			const parts = key.split('-');
			const concept = parts.pop();
			const payerType = parts.pop();
			const propertyId = parts.join('-');
			const mapKey = `${propertyId}-${payerType}`;
			if (!byKey[mapKey]) byKey[mapKey] = { property_id: parseInt(propertyId), payer_type: payerType, fields: [] };
			byKey[mapKey].fields.push(concept);
		}
		return Object.values(byKey);
	};

	const handleReplace = async () => {
		if (checkedCount === 0) return;
		setLoading(true);

		await axios.post(`/api/manager/owners/${ownerId}/replace-bankaccount`, {
			community_id: getCommunity()?.id,
			new_bankaccount_id: newBankAccount.id,
			replacements: buildReplacements(),
		}, {
			cancelToken: axiosCancelToken.token,
		}).then(() => {
			toast.success('Cuenta sustituida correctamente');
			Modal.getInstance(modalRef.current)?.hide();
		}).catch((error) => {
			if (axios.isCancel(error)) return;
			toast.error('Error al sustituir la cuenta');
			setLoading(false);
		});
	};

	const renderConceptCell = (property, concept) => {
		const conceptData = property.concepts[concept];
		const payerType = conceptData?.payer_type;

		if (!payerType) {
			return <ConceptCell key={concept} className="empty">—</ConceptCell>;
		}

		const key = `${property.property_id}-${payerType}-${concept}`;
		const isChecked = !!checked.get(key);
		const acc = conceptData?.current_account;
		const ibanLabel = getAccountLabel(acc);
		const typeLabel = payerType === 'owner' ? 'Propietario' : 'Inquilino';
		const payerName = conceptData?.payer_name ?? null;

		return (
			<ConceptCell
				key={concept}
				className="has-payer"
				onClick={() => toggleCheck(key)}
			>
				<PayerName>
					{payerName && <span>{payerName} </span>}
					<em>({typeLabel})</em>
				</PayerName>
				<PayerIban>{ibanLabel}</PayerIban>
				<CheckLabel onClick={e => e.stopPropagation()}>
					<input
						type="checkbox"
						checked={isChecked}
						onChange={() => toggleCheck(key)}
					/>
					¿Sustituir?
				</CheckLabel>
			</ConceptCell>
		);
	};

	return (
		<ModalStyled className="modal" tabIndex="-1" ref={modalRef}>
			<div className="modal-dialog modal-xl">
				<div className="modal-content">
					<div className="modal-header border-0 pb-0">
						<div>
							<h5 className="modal-title mb-0">Sustituir cuenta en propiedades</h5>
							<IbanSubtitle>{formatIbanGroups(newBankAccount.iban)}</IbanSubtitle>
						</div>
						<button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>

					<div className="modal-body">
						<p className="text-muted small mb-3">
							Marca los conceptos donde quieres usar esta cuenta como nueva cuenta de cobro.
						</p>

						<div className="table-responsive">
							<Table>
								<thead>
									<tr>
										<th>Propiedad</th>
										{CONCEPTS.map(c => <th key={c}>{CONCEPT_LABELS[c]}</th>)}
									</tr>
								</thead>
								<tbody>
									{properties.map(property => (
										<tr key={property.property_id}>
											<PropertyCell>{property.property_name}</PropertyCell>
											{CONCEPTS.map(c => renderConceptCell(property, c))}
										</tr>
									))}
								</tbody>
							</Table>
						</div>
					</div>

					<div className="modal-footer border-0 pt-0">
						<button
							type="button"
							className="btn btn-sm btn-light"
							data-bs-dismiss="modal"
						>
							No sustituir
						</button>
						<button
							type="button"
							className="btn btn-sm btn-primary text-white"
							disabled={checkedCount === 0}
							onClick={handleReplace}
						>
							Sustituir en {checkedCount} concepto{checkedCount !== 1 ? 's' : ''}
						</button>
					</div>
				</div>
			</div>
			{loading && <CristalLoader />}
		</ModalStyled>
	);
}
